"use client"

import type React from "react"
import { createContext, useContext, useState, useEffect } from "react"
import { type Language, translations, type TranslationKey } from "@/lib/i18n"

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: TranslationKey) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

/**
 * 检测浏览器语言并映射到支持的语言
 * @returns 支持的语言代码
 */
function detectBrowserLanguage(): Language {
  if (typeof window === "undefined") {
    return "zh" // 服务端渲染时的默认语言
  }

  // 获取浏览器语言列表
  const browserLanguages = navigator.languages || [navigator.language]

  // 语言映射表
  const languageMap: Record<string, Language> = {
    // 中文
    zh: "zh",
    "zh-CN": "zh",
    "zh-TW": "zh",
    "zh-HK": "zh",
    "zh-SG": "zh",
    // 英文
    en: "en",
    "en-US": "en",
    "en-GB": "en",
    "en-AU": "en",
    "en-CA": "en",
    // 日文
    ja: "ja",
    "ja-JP": "ja",
    // 韩文
    ko: "ko",
    "ko-KR": "ko",
  }

  // 遍历浏览器语言列表，找到第一个支持的语言
  for (const browserLang of browserLanguages) {
    const normalizedLang = browserLang.toLowerCase()

    // 完全匹配
    if (languageMap[normalizedLang]) {
      console.log(`检测到浏览器语言: ${browserLang}, 映射到: ${languageMap[normalizedLang]}`)
      return languageMap[normalizedLang]
    }

    // 部分匹配（只匹配语言代码，忽略地区代码）
    const langCode = normalizedLang.split("-")[0]
    if (languageMap[langCode]) {
      console.log(`检测到浏览器语言: ${browserLang}, 通过语言代码 ${langCode} 映射到: ${languageMap[langCode]}`)
      return languageMap[langCode]
    }
  }

  // 如果没有匹配到任何支持的语言，默认使用中文
  console.log(`未检测到支持的浏览器语言，使用默认语言: zh`)
  return "zh"
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>("zh") // 初始默认值
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    // 只在客户端执行语言检测和初始化
    if (typeof window !== "undefined" && !isInitialized) {
      // 首先检查本地存储中是否有用户之前选择的语言
      const savedLanguage = localStorage.getItem("language") as Language

      if (savedLanguage && translations[savedLanguage]) {
        console.log(`从本地存储恢复语言设置: ${savedLanguage}`)
        setLanguage(savedLanguage)
      } else {
        // 如果没有保存的语言设置，则检测浏览器语言
        const detectedLanguage = detectBrowserLanguage()
        console.log(`自动检测并设置语言: ${detectedLanguage}`)
        setLanguage(detectedLanguage)
        // 将检测到的语言保存到本地存储
        localStorage.setItem("language", detectedLanguage)
      }

      setIsInitialized(true)
    }
  }, [isInitialized])

  const handleSetLanguage = (lang: Language) => {
    console.log(`用户手动切换语言: ${language} -> ${lang}`)
    setLanguage(lang)
    if (typeof window !== "undefined") {
      localStorage.setItem("language", lang)
    }
  }

  const t = (key: TranslationKey): string => {
    return translations[language][key] || translations.zh[key] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}
